<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  message: { type: Object, required: true },
});
const emit = defineEmits(['close']);
const { t, te } = useI18n();
const store = useStore();
const dialog = ref(null);
const textarea = ref(null);
const originalContent = props.message.content;
const content = ref(originalContent);
const isSaving = ref(false);
const error = ref('');
const disabled = computed(
  () => !content.value.trim() || content.value === originalContent
);

onMounted(async () => {
  dialog.value.open();
  await nextTick();
  textarea.value?.focus();
});

async function save() {
  if (disabled.value || isSaving.value) return;
  isSaving.value = true;
  error.value = '';
  try {
    await store.dispatch('editMessageContent', {
      conversationId:
        props.message.conversation_id ?? props.message.conversationId,
      messageId: props.message.id,
      content: content.value,
      originalContent,
    });
    dialog.value?.close();
  } catch (exception) {
    const key = `GOWA_MESSAGE_EDIT.ERRORS.${exception.response?.data?.code}`;
    error.value = t(
      te(key) ? key : 'GOWA_MESSAGE_EDIT.ERRORS.EDIT_UNCONFIRMED'
    );
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t('GOWA_MESSAGE_EDIT.TITLE')"
    :description="t('GOWA_MESSAGE_EDIT.DESCRIPTION')"
    :confirm-button-label="t('GOWA_MESSAGE_EDIT.SAVE')"
    :disable-confirm-button="disabled"
    :is-loading="isSaving"
    @confirm="save"
    @close="emit('close')"
  >
    <label class="flex flex-col gap-2 text-sm text-n-slate-12">
      {{ t('GOWA_MESSAGE_EDIT.TEXT') }}
      <textarea
        ref="textarea"
        v-model="content"
        :disabled="isSaving"
        rows="6"
        maxlength="4096"
        class="w-full resize-y rounded-lg border border-n-weak bg-n-background p-3 text-sm text-n-slate-12 focus:border-n-brand"
      />
    </label>
    <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
      {{ error }}
    </p>
  </Dialog>
</template>
